/**
 * types/transient/BetaApplicationDraft.ts
 * Beta application draft data structure
 *
 * Holds ALL beta application form data across the 3-step wizard flow.
 * Persisted in localStorage during application to prevent data loss.
 * Converted to application submission data when wizard completes.
 */

// Internal absolute imports
import type {
  TradingFocus,
  ExperienceLevel,
  TradingStyle,
  BetaAccountType,
  TradingSessions,
  TestingCommitment,
  FeedbackCommitment,
  BetaApplicationStep,
  PropFirmStatus,
  PropFirmPayoutHistory
} from '@/components/BetaApplication/types/BetaApplicationTypes';

export interface BetaApplicationDraft extends Record<string, unknown> {
  /** When this draft was created */
  createdAt: string;

  /** When this draft was last modified */
  lastModified: string;

  /** Current step user is on */
  currentStep: BetaApplicationStep;

  /** Steps that have been completed */
  completedSteps: Array<BetaApplicationStep>;

  // Step 1: Account Information
  account: {
    /** User's email address (required) */
    email: string;

    /** User's full name (required) */
    fullName: string;

    /** Discord username (required) */
    discordUsername: string;
  };

  // Step 2: Trading Profile
  trading: {
    /** Primary trading focus (required) - multiple assets allowed */
    primaryFocus: TradingFocus[];

    /** Trading experience level (required) */
    experienceLevel: ExperienceLevel | '';

    /** Primary trading style (required) */
    tradingStyle: TradingStyle | '';

    /** Trading style other specification (when tradingStyle is 'other') */
    tradingStyleOther?: string;

    /** Type of trading account (required) */
    accountType: BetaAccountType | '';

    /** Prop firm name (if using prop firm account) */
    propFirm: string;

    /** Prop firm status (if using prop firm account) */
    propFirmStatus: PropFirmStatus | '';

    /** Prop firm payout history (if funded prop firm account) */
    propFirmPayoutHistory: PropFirmPayoutHistory | '';

    /** Broker name (required) */
    broker: string;

    /** Primary trading sessions (required) - multiple sessions allowed */
    tradingSessions: TradingSessions[];

    /** Trading sessions other specification (when tradingSessions includes 'other') */
    tradingSessionsOther?: string;

    /** Current journaling method (required) */
    currentJournaling: string;
  };

  // Step 3: Beta Commitment
  commitment: {
    /** Interest in beta testing (required) */
    stjInterest: string;

    /** Testing commitment level (required) */
    testingCommitment: TestingCommitment | '';

    /** Feedback participation commitment (required) */
    feedbackCommitment: FeedbackCommitment | '';
  };
}

/**
 * Creates a default beta application draft with all required fields initialized
 * @returns Complete draft object ready for wizard use
 */
export function createDefaultBetaApplicationDraft(): BetaApplicationDraft {
  const now = new Date().toISOString();

  return {
    createdAt: now,
    lastModified: now,
    currentStep: 'account',
    completedSteps: [],

    // Step 1: Account Information - all empty initially
    account: {
      email: '',
      fullName: '',
      discordUsername: ''
    },

    // Step 2: Trading Profile - all empty initially
    trading: {
      primaryFocus: [],
      experienceLevel: '',
      tradingStyle: '',
      tradingStyleOther: '',
      accountType: '',
      propFirm: '',
      propFirmStatus: '',
      propFirmPayoutHistory: '',
      broker: '',
      tradingSessions: [],
      tradingSessionsOther: '',
      currentJournaling: ''
    },

    // Step 3: Beta Commitment - all empty initially
    commitment: {
      stjInterest: '',
      testingCommitment: '',
      feedbackCommitment: ''
    }
  };
}